/** @format */
import { DOCUMENT, ViewportScroller } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

import { MetaService, SeoService } from '@seo/data-access';
import { AuthenticationService } from '@shared/authentication/data-access';

import { environment } from '@environment/myaccount';

import { MYACCOUNT_CONSTANTS } from '@myaccount/app/app.constants';

import { LoyaltyBalance } from '@scorecard/data-access';

import { Chain, Customer, PersonalInformation } from '@shared/data-access/interfaces';

import {
	CartService,
	CookieService,
	EventBusService,
	Events,
	HttpService,
	KillswitchService,
	MyAccountAnalyticsService,
	Store,
	StoreService,
	UtilityService
} from '@shared/data-access/services';

import { HomrDataService } from '@shared/homr/data-access';

import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map, mergeMap, take } from 'rxjs/operators';

@Component({
	selector: 'myaccount-root',
	templateUrl: './app.component.html',
	styleUrl: './app.component.scss',
	standalone: false
})
export class AppComponent implements OnInit {
	/**
	 * @description
	 * The chain identifier abbreviation
	 */
	public chainIdentifierAbbr = this.chain.chainIdentifierAbbr;

	/**
	 * @description
	 * Used to determine whether or not the canonicals have been created/set
	 */
	public canonicalsCreated = false;

	/**
	 * @description
	 * Used to determine whether or not the ticker name has been set to the product ID
	 */
	public isTickerPageNameSet = false;

	public isPageWithSimpleHeader = false;

	/**
	 * @description
	 * The async events to count for reporting:PostPageRender
	 *
	 * @private
	 */
	private postPageRenderEventsToCount: Events[] = [];

	/**
	 * @description
	 * The async events to observe for reporting:PostPageRender
	 *
	 * @private
	 */
	private postPageRenderEventsToObserve: Events[] = [
		Events.LoyaltyBalanceFetched,
		Events.LoyaltyProfileFetched
	];

	/**
	 * @description
	 * Async Events observable for reporting:PostPageRender
	 *
	 * @private
	 */
	private postPageRenderEventsObservable = new BehaviorSubject(null);

	constructor(
		private analyticsService: MyAccountAnalyticsService,
		private authService: AuthenticationService,
		private cartService: CartService,
		private chain: Chain,
		private cookieService: CookieService,
		@Inject(DOCUMENT) private document: HTMLDocument,
		private eventBusService: EventBusService,
		private homrDataService: HomrDataService,
		private metaService: MetaService,
		private route: ActivatedRoute,
		private router: Router,
		private seoService: SeoService,
		private store: Store,
		private storeService: StoreService,
		private title: Title,
		private utilityService: UtilityService,
		private viewportScroller: ViewportScroller,
		private killSwitchService: KillswitchService,
		private httpService: HttpService
	) {
		// Check authentication status and set up authentication status stream
		this.authService.localAuthSetup();

		router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
			// Angular v7+
			this.viewportScroller.scrollToPosition([0, 0]);
		});
	}

	ngOnInit(): void {
		// Reset the analytics
		this.analyticsService.resetAnalytics();

		/*
		 * Cart
		 */
		// Initialize the Cart Service
		this.cartService.initialize();

		/*
		 * Auth
		 */
		// Check cookie and populate Store
		this.authService.checkIfReturningCustomer();

		/*
		 * DSG_UID cookie
		 */
		if (!this.cookieService.check('DSG_UID')) {
			// Create a DSG_UID cookie
			this.cookieService.set('DSG_UID', this.utilityService.generateUUID(), 365, '/', '', '');
		}

		/*
		 * WCS Virtual Host cookie
		 */
		// Virtual Host chain identifier
		if (
			typeof this.chain.chainIdentifierCookie !== 'undefined' &&
			!this.cookieService.check('DSG_VH')
		) {
			this.cookieService.set('DSG_VH', this.chain.chainIdentifierCookie, '', '/', '', '');
		}

		// Render the simple header component. This should only be done for paths that aren't loading the react header (see marginals-react.js).
		if (
			this.httpService.getUrlPath() === '/CompleteProfile' ||
			this.httpService.getUrlPath() === '/ChangeEmail'
		) {
			this.isPageWithSimpleHeader = true;
		}

		// Subscribe to the router events and filter to only `NavigationEnd`
		this.router.events
			.pipe(
				filter((event) => event instanceof NavigationEnd),
				map(() => this.route),
				map((route) => {
					while (route.firstChild) {
						route = route.firstChild;
					}
					return route;
				}),
				mergeMap(
					(route: ActivatedRoute) =>
						new Observable((observer) => {
							observer.next({
								data: route.data,
								params: route.params,
								queryParams: route.queryParams
							});
							observer.complete();
						})
				)
			)
			.subscribe((route: any) => {
				// Get a Promise for route.data
				const dataPromise = route.data.pipe(take(1)).toPromise();

				// Get a Promise for route.params
				const paramsPromise = route.params.pipe(take(1)).toPromise();

				// Get a Promise for route.queryParams
				const queryParamsPromise = route.queryParams.pipe(take(1)).toPromise();

				// Wait until all of the Promises have been resolved
				Promise.all([dataPromise, paramsPromise, queryParamsPromise]).then(async (data) => {
					// Get the current route path parameters from the route params
					// const CURRENT_PARAMS = data[1]; // TODO: Keep until we determine we don't need in the future

					// Get the current query parameters from the route query params
					const CURRENT_QUERY_PARAMS = data[2];

					// Get the current Date/Time
					const CURRENT_DATETIME = new Date();

					/*
					 * homR
					 */
					// Set the homR page name
					this.homrDataService.setPageName('myaccount');

					// Set the flag to true to start rendering the ticker
					this.isTickerPageNameSet = true;

					/*
					 * Analytics
					 */
					// Set the chain
					this.analyticsService.setChain(this.chain.chainName);

					// Set the Error ID (if present)
					this.analyticsService.setErrorId();

					// Set the Error Message (if present)
					this.analyticsService.setErrorMessage();

					// Set the referring URL
					this.analyticsService.setReferringPageUrl(this.document.referrer);

					// Set the Page Type
					this.analyticsService.setType('page', 'My Account Page');

					// Set the server date
					this.analyticsService.setServerDate(
						this.analyticsService.getFormattedDate(CURRENT_DATETIME)
					);

					// Set the server time
					this.analyticsService.setServerTime(
						this.analyticsService.getFormattedTime(CURRENT_DATETIME)
					);

					// Get the Customer from state and populate related analytics
					const customer: Customer = this.store.value['CUSTOMER'] || new Customer();

					// Set the Loyalty ID
					this.analyticsService.setLoyaltyID(customer.x_acNumber || '');

					// Set the LoyaltyBalance
					this.analyticsService.setLoyaltyPointsBalance(customer.x_acBalancePoints || '');

					// Set the session ID
					this.analyticsService.setSessionID(customer.sessionId);

					// Set the user ID
					this.analyticsService.setUserID(customer.userId);

					// Set the visits count
					this.analyticsService.setVisitNumber(customer.visitCount);

					// Check to see if the logonId is set
					if (customer.logonId) {
						// Set the hashed email string
						this.analyticsService.setEAthlete(
							this.utilityService.hashString(customer.logonId)
						);
					}

					// Set the base Search properties
					this.analyticsService.setSearchPageBaseProperties({
						searchDetails: {
							DYMTerm:
								CURRENT_QUERY_PARAMS.dymterm || CURRENT_QUERY_PARAMS.DYMTerm || '',
							DYMType:
								CURRENT_QUERY_PARAMS.didyoumean ||
								CURRENT_QUERY_PARAMS.didYouMean ||
								'',
							replacementTerm: CURRENT_QUERY_PARAMS.replacementTerm || '',
							searchTerm:
								CURRENT_QUERY_PARAMS.enteredSearchTerm ||
								CURRENT_QUERY_PARAMS.searchTerm ||
								'',
							searchType: CURRENT_QUERY_PARAMS.searchType || ''
						}
					});

					// Validate authentication status
					const isAuthenticated = await this.authService
						.getIsAuthenticated$()
						.toPromise()
						.catch((err) => {
							this.analyticsService.setErrorId(
								'DCSG_NGX_MYACCOUNT_AUTHENTICATED_ERROR'
							);
							this.analyticsService.setErrorMessage(`MYACCOUNT:${err.message}`);

							// Return false because there was an error validating authentication status
							return false;
						});

					// Set the logged in flag
					this.analyticsService.setLoggedIn(`${isAuthenticated}`);

					// Set the LoggedInOccured property
					this.analyticsService.setLoggedInOccurred(`${isAuthenticated}`);

					if (
						MYACCOUNT_CONSTANTS.unAuthenticatedAppPaths?.some((s) =>
							this.httpService.getUrlPath().includes(s)
						)
					) {
						// trigger the NewPageView event on all unauthenticated pages
						this.analyticsService.triggerAnalyticsEvent('NewPageView');
					} else if (
						!MYACCOUNT_CONSTANTS.appointmentsPath.includes(
							this.httpService.getUrlPath()
						) &&
						!this.httpService.getUrlPath().includes(MYACCOUNT_CONSTANTS.listsPath)
					) {
						// otherwise, this is an authenticated page, so fire the NewPageEvent only when the loyalty balance and loyalty profile info are finished loading
						// exclude the Appointments and lists pages, which fire on their own
						this.postPageRenderEventTriggerSequence();
					}

					/*
					 * Promos
					 */
					// Check to see if the `param` query parameter is present
					if (
						typeof CURRENT_QUERY_PARAMS.param === 'string' &&
						CURRENT_QUERY_PARAMS.param.trim() !== '' &&
						!this.cookieService.check('promoId')
					) {
						// Set the promoId cookie
						this.cookieService.set(
							'promoId',
							CURRENT_QUERY_PARAMS.param,
							'',
							'/',
							'',
							''
						);
					}

					/*
					 * SEO
					 */
					// Check to see if the page title is available
					if (typeof data[0].title === 'string' && data[0].title.trim() !== '') {
						// Set the document title
						this.title.setTitle(`${data[0].title} | ${this.chain.chainName}`);
					}

					// Check to see if the page meta data is available
					if (typeof data[0].meta === 'string' && data[0].meta.trim() !== '') {
						// Get and update the meta description
						const metaDescription = data[0].meta
							.replace('{0}', this.chain.chainName)
							.replace('{1}', this.chain.chainName);

						// Set the meta description
						this.metaService.addTag(
							{ name: 'description', content: metaDescription },
							true
						);
					}

					// Canonical URL
					const canonicalUrl = `${this.chain.metaCanonical}${this.router.url.split('?')[0]}`;

					// Check the current chain
					if (this.chain.chainIdentifierAbbr !== 'dsg') {
						this.initializeSEOContent(route);
					} else {
						this.setCanonicalTag(canonicalUrl);
					}

					// OpenGraph
					this.metaService.addTag({ property: 'og:url', content: canonicalUrl }, true);
				});
			});

		/*
		 * DOM Interactions
		 */
		// eliminate 'persistent' click focus
		document.body.addEventListener('mousedown', () => {
			document.body.classList.add('mouse-Event');
		});

		document.body.addEventListener('keydown', () => {
			document.body.classList.remove('mouse-Event');
		});

		/*
		 * Third-party
		 */
		this.storeService.initialize();

		// Check if Session Cam is enabled
		if (environment.thirdParty.sessionCam.enabled) {
			// Create the script element
			const sessionCamScript = this.document.createElement('script');

			// Set the `type` attribute
			sessionCamScript.setAttribute('type', 'text/javascript');

			// Set the innerHTML
			sessionCamScript.innerHTML = `
					(function() {
						if (!window.sessioncamConfiguration) {
							window.sessioncamConfiguration = {};
						}
						if (!window.sessioncamConfiguration.customDataObjects) {
							window.sessioncamConfiguration.customDataObjects = [];
						}
						window.sessioncamConfiguration.customDataObjects.push({
							key: "AdobeTarget",
							value: "${this.chain.sessionCamIdentifier}"
						});
						window.sessioncamConfiguration.customDataObjects.push({
							key: "ApplicationName",
							value: "${this.chain.sessionCamIdentifier}"
						});
						window.sessioncamConfiguration.SessionCamPath = "res-myaccount";
					})();
				`;

			// Append script to the head element
			this.document.head.appendChild(sessionCamScript);
		}

		// set killswitch values
		this.killSwitchService.getKillSwitchesEnvironment$().subscribe((data) => {
			this.store.set('ENVIRONMENT_KILLSWITCH_VALUES', data);
		});

		// enable BV for product reviews
		this.utilityService.loadExternalScript(
			environment.thirdParty.bv.store[this.chain.chainIdentifierAbbr].script
		);
	}

	/**
	 * @description
	 * Calls the SEO service to attempt to get SEO data
	 */
	private initializeSEOContent(route) {
		this.seoService
			.getSEOData$(route.params.value.seoToken)
			.toPromise()
			.then((seodata) => {
				if (seodata.categories && seodata.categories[0]) {
					if (seodata.categories[0].attr.SEO_CANONICAL) {
						this.setCanonicalTag(
							seodata.categories[0].attr.SEO_CANONICAL.value,
							'SEO Service'
						);
					} else {
						this.setCanonicalTag(
							`${this.chain.metaCanonical}${this.router.url.split('?')[0]}`
						);
					}
				} else {
					this.setCanonicalTag(
						`${this.chain.metaCanonical}${this.router.url.split('?')[0]}`
					);
				}
			});
	}

	private postPageRenderEventTriggerSequence() {
		/*
		 * Analytics async logic
		 */
		// Observe all events passed in to determine if we can trigger analytics
		const postPageRenderEventsSubscription = this.postPageRenderEventsObservable.subscribe(
			() => {
				// Count of matches
				let matchCount = 0;

				// Check to see if there are any events to count
				if (this.postPageRenderEventsToCount !== undefined) {
					// Iterate the required events to match
					this.postPageRenderEventsToObserve.forEach((eventType: Events) => {
						// Check to see if the current event has been triggered
						if (this.postPageRenderEventsToCount.indexOf(eventType) > -1) {
							// Match found, increment counter
							matchCount += 1;
						}
					});

					// Check to see if we matched all required events
					if (matchCount === this.postPageRenderEventsToObserve.length) {
						// Reset the events to count so that it starts over when we reach our match count
						this.postPageRenderEventsToCount = [];

						// Unsubscribe since we have all the data we need
						postPageRenderEventsSubscription.unsubscribe();

						// Trigger analytics `NewPageView` event
						this.analyticsService.triggerAnalyticsEvent('NewPageView');
					}
				}
			}
		);

		/// Checking for previous emitted events so the listener doesn't miss a beat
		if (this.eventBusService.checkHistoryForEvent(Events.LoyaltyBalanceFetched)) {
			this.postPageRenderEventsToCount.push(Events.LoyaltyBalanceFetched);
			this.postPageRenderEventsObservable.next(Events.LoyaltyBalanceFetched);
		}

		// Listen for the LoyaltyBalance Event
		const loyaltyBalanceFetchedSubscription = this.eventBusService.on(
			Events.LoyaltyBalanceFetched,
			(loyaltyBalance: LoyaltyBalance) => {
				// Check to see if the Event has already been triggered
				if (
					Array.isArray(this.postPageRenderEventsToCount) &&
					this.postPageRenderEventsToCount.indexOf(Events.LoyaltyBalanceFetched) === -1
				) {
					// Set the LoyaltyPointsBalance
					this.analyticsService.setLoyaltyPointsBalance(
						`${loyaltyBalance?.currentPointBalance || ''}`
					);

					// Add incoming event to the Array
					this.postPageRenderEventsToCount.push(Events.LoyaltyBalanceFetched);

					// Publish the event to the Subscription
					this.postPageRenderEventsObservable.next(Events.LoyaltyBalanceFetched);

					// Unsubscribe after we trigger next on the parent observable
					loyaltyBalanceFetchedSubscription.unsubscribe();
				}
			}
		);

		if (this.eventBusService.checkHistoryForEvent(Events.LoyaltyProfileFetched)) {
			this.postPageRenderEventsToCount.push(Events.LoyaltyProfileFetched);
			this.postPageRenderEventsObservable.next(Events.LoyaltyProfileFetched);
		}

		// Listen for the LoyaltyProfileFetched Event
		const loyaltyProfileFetchedSubscription = this.eventBusService.on(
			Events.LoyaltyProfileFetched,
			(personalInformation: PersonalInformation) => {
				// Check to see if the Event has already been triggered
				if (
					Array.isArray(this.postPageRenderEventsToCount) &&
					this.postPageRenderEventsToCount.indexOf(Events.LoyaltyProfileFetched) === -1
				) {
					// Set the LoyaltyID
					this.analyticsService.setLoyaltyID(personalInformation?.loyaltyNumber || '');

					// Add incoming event to the Array
					this.postPageRenderEventsToCount.push(Events.LoyaltyProfileFetched);

					// Publish the event to the Subscription
					this.postPageRenderEventsObservable.next(Events.LoyaltyProfileFetched);

					// Unsubscribe after we trigger next on the parent observable
					loyaltyProfileFetchedSubscription.unsubscribe();
				}
			}
		);
	}

	/**
	 * @description
	 * Checks to see if a canonical URL has been created or not; If not created, it
	 * will create one and set the URL; If already created, it will query the DOM
	 * and update the URL
	 */
	private setCanonicalTag(url: string, comment = 'Default') {
		if (!this.canonicalsCreated) {
			// Create a link element
			const canonicalLink = this.document.createElement('link');

			// Set the link relationship attribute
			canonicalLink.setAttribute('rel', 'canonical');

			// Set the link href attribute
			canonicalLink.setAttribute('href', url);

			// Append a comment for the canonical
			this.document.head.appendChild(this.document.createComment(`${comment} Canonical`));

			// Append the link element to the head element
			this.document.head.appendChild(canonicalLink);

			// Set the created flag to true so that multiple canonicals are not created
			this.canonicalsCreated = true;
		} else {
			// Canonical element is already created, update the URL on route change
			this.document.head.querySelector('[rel="canonical"]').setAttribute('href', url);
		}
	}
}
